import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';

const VehiclePhotosModal = ({ vehicle, onClose, allowDelete = true }) => {
  const [photos, setPhotos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVehiclePhotos();
  }, [vehicle.id]);

  const fetchVehiclePhotos = async () => {
    try {
      const response = await axios.get(`/api/vehicles/${vehicle.id}/photos`);
      setPhotos(response.data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const deletePhoto = async (photoId) => {
    if (window.confirm('Delete this photo?')) {
      try {
        await axios.delete(`/api/vehicles/photos/${photoId}`);
        toast.success('Photo deleted');
        fetchVehiclePhotos();
        if (currentIndex >= photos.length - 1) {
          setCurrentIndex(Math.max(0, photos.length - 2));
        }
      } catch (error) {
        toast.error('Failed to delete photo');
      }
    }
  };

  const nextPhoto = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
        <div className="text-white">Loading photos...</div>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Vehicle Photos</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-gray-500 text-center py-8">No photos available for this vehicle.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="relative w-full h-full flex items-center justify-center">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
        >
          <X className="w-8 h-8" />
        </button>

        {/* Navigation buttons */}
        {photos.length > 1 && (
          <>
            <button
              onClick={prevPhoto}
              className="absolute left-4 text-white hover:text-gray-300 z-10"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            <button
              onClick={nextPhoto}
              className="absolute right-4 text-white hover:text-gray-300 z-10"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          </>
        )}

        {/* Delete button */}
        {allowDelete && (
          <button
            onClick={() => deletePhoto(photos[currentIndex].id)}
            className="absolute top-4 left-4 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full z-10"
            title="Delete Photo"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}

        {/* Main image */}
        <div className="max-w-4xl max-h-full p-4">
          <img
            src={`http://localhost:5000${photos[currentIndex].photoUrl}`}
            alt={`Vehicle photo ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>

        {/* Photo counter */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full">
          {currentIndex + 1} / {photos.length}
        </div>

        {/* Thumbnails */}
        {photos.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 mt-16">
            {photos.map((photo, index) => (
              <button
                key={photo.id}
                onClick={() => setCurrentIndex(index)}
                className={`w-16 h-16 rounded-md overflow-hidden border-2 ${
                  index === currentIndex ? 'border-white' : 'border-transparent'
                }`}
              >
                <img
                  src={`http://localhost:5000${photo.photoUrl}`}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VehiclePhotosModal;
